import { createRoute } from "../../routing/createRoute.js";

/**
 * One administrative page, as the search field sees it.
 *
 * `keywords` is what makes the field useful before someone knows what the pages
 * are called: they are the words a person would actually type, not synonyms of
 * the label. A page reachable only by knowing its name is a page nobody finds,
 * and this list is expected to grow well past what a menu can show at once.
 *
 * Deliberately carries no component. The router owns which page renders at which
 * path, so a menu entry that also named a component would be a second place
 * deciding that — and the two would disagree the first time a route moved.
 */
export type AdminPage = {
  id: string;
  labelKey: string;
  path: string;
  keywords: string[];
};

export type AdminPageGroup = {
  id: string;
  labelKey: string;
  pages: AdminPage[];
};

export const adminPageGroups: AdminPageGroup[] = [
  {
    id: "directory",
    labelKey: "admin.groups.directory",
    pages: [
      {
        id: "users",
        labelKey: "admin.users.title",
        path: createRoute.adminUsers(),
        keywords: [
          "users",
          "accounts",
          "people",
          "staff",
          "assessors",
          "managers",
          "email",
          "sign in",
        ],
      },
      {
        id: "organisations",
        labelKey: "admin.organisations.title",
        path: createRoute.adminOrganisations(),
        keywords: [
          "organisations",
          "orgs",
          "local authorities",
          "councils",
          "trusts",
          "customers",
          "tenants",
        ],
      },
    ],
  },
  {
    id: "configuration",
    labelKey: "admin.configuration.title",
    pages: [
      {
        id: "configuration-chain",
        labelKey: "admin.configuration.scopesTitle",
        path: createRoute.adminConfigurationScopes(),
        keywords: [
          "configuration",
          "chain",
          "scope",
          "scopes",
          "programme",
          "cooperative",
          "rcc",
          "pilot",
          "inheritance",
          "feature flags",
          "settings",
        ],
      },
      {
        id: "configuration-organisations",
        labelKey: "admin.configuration.organisationsTitle",
        path: createRoute.adminOrganisationConfiguration(),
        keywords: [
          "configuration",
          "organisation settings",
          "feature flags",
          "assessment versions",
          "language",
          "vocabulary",
          "attach",
          "entitlements",
        ],
      },
    ],
  },
  {
    id: "oversight",
    labelKey: "admin.groups.oversight",
    pages: [
      {
        id: "audit-trail",
        labelKey: "admin.trail.title",
        path: createRoute.adminAuditTrail(),
        keywords: [
          "audit",
          "trail",
          "history",
          "log",
          "who did",
          "activity",
          "access",
          "sign in",
          "logins",
          "events",
        ],
      },
      {
        id: "persons",
        labelKey: "admin.persons.title",
        path: createRoute.adminPersons(),
        keywords: [
          "profile",
          "young person",
          "involvement",
          "case manager",
          "unassigned",
          "id",
          "link",
        ],
      },
    ],
  },
];

export const adminPages: AdminPage[] = adminPageGroups.flatMap(
  (group) => group.pages,
);

/**
 * The pages matching what someone has typed, grouped as the menu shows them.
 *
 * Matched against the translated label as well as the keywords, because the
 * label is what is on screen — someone typing what they can see must find it.
 * An empty search matches everything, so the field doubles as the menu.
 */
export const matchingAdminPages = (
  search: string,
  label: (key: string) => string,
): AdminPageGroup[] => {
  const term = search.trim().toLowerCase();

  if (term.length === 0) {
    return adminPageGroups;
  }

  return adminPageGroups
    .map((group) => ({
      ...group,
      pages: group.pages.filter(
        (page) =>
          label(page.labelKey).toLowerCase().includes(term) ||
          page.keywords.some((keyword) => keyword.includes(term)),
      ),
    }))
    .filter((group) => group.pages.length > 0);
};
